// src/pages/PrivacyPolicy.js
import React from 'react';
import { Link } from 'react-router-dom';
import BrandName from '../components/BrandName';
import RegisterCheck from '../components/RegisterCheck';
import Seo from '../components/Seo';
import { SIMPLE_PAGES } from '../data/simplePages';
import { webPageJsonLd } from '../data/pageSchema';
import './LegalPage.css';

const seo = SIMPLE_PAGES.find((page) => page.path === '/privacy');

const LAST_UPDATED = '27 October 2026';

const Section = ({ title, children }) => (
  <div className="legal-section">
    <h2 className="legal-section-title">{title}</h2>
    {children}
  </div>
);

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={webPageJsonLd(seo)} />
      <div className="legal-hero">
        <div className="page-wrapper">
          <p className="legal-label">Legal</p>
          <h1 className="legal-title">Privacy <span>Policy</span></h1>
          <p className="legal-meta">Last updated: {LAST_UPDATED} · Compliant with UK GDPR &amp; the Data Protection Act 2018</p>
        </div>
      </div>

      <div className="page-wrapper">
        <div className="legal-body">

          <Section title="1. Who We Are">
            <p><BrandName inline /> ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") is the data controller for personal data collected through this Platform. We are based in Swansea, Wales, UK.</p>
            <p>Contact: <a className="legal-link" href="mailto:info@thewastegame.co.uk">info@thewastegame.co.uk</a></p>
          </Section>

          <Section title="2. What Data We Collect">
            <p>We collect the following categories of personal data:</p>
            <div className="legal-table-wrap">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Data collected</th>
                    <th>Who it applies to</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account data</td>
                    <td>Full name, email address, account type (customer / carrier), date of registration</td>
                    <td>All users</td>
                  </tr>
                  <tr>
                    <td>Profile photos</td>
                    <td>Optional profile photo uploaded by the user</td>
                    <td>All users</td>
                  </tr>
                  <tr>
                    <td>Location data</td>
                    <td>Pickup postcode / address entered on job posts</td>
                    <td>Customers</td>
                  </tr>
                  <tr>
                    <td>Regulatory data</td>
                    <td>Waste carrier licence number (Environment Agency or Natural Resources Wales), vehicle type</td>
                    <td>Carriers</td>
                  </tr>
                  <tr>
                    <td>Job data</td>
                    <td>Service type, job description, date, budget, status history</td>
                    <td>All users</td>
                  </tr>
                  <tr>
                    <td>Transaction data</td>
                    <td>Bid amounts, accepted prices, Waste Transfer Notes, Stripe payment intent IDs, payment status</td>
                    <td>All users</td>
                  </tr>
                  <tr>
                    <td>Usage data</td>
                    <td>Eco Credits, ratings, reviews, leaderboard position, badge history, weekly challenge progress</td>
                    <td>Carriers</td>
                  </tr>
                  <tr>
                    <td>Referral data</td>
                    <td>Referring carrier name, referred carrier name, associated job context</td>
                    <td>Carriers</td>
                  </tr>
                  <tr>
                    <td>Communications</td>
                    <td>In-platform notifications, bid notes</td>
                    <td>All users</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>We do <strong>not</strong> collect or store payment card details or bank account information. All payment data is handled exclusively by Stripe — see Section 5.</p>
          </Section>

          <Section title="3. Why We Collect It (Legal Basis)">
            <p>We process your personal data on the following legal bases under UK GDPR Article 6:</p>
            <ul className="legal-list">
              <li><strong>Contract performance (Art. 6(1)(b))</strong> — to create and manage your account, match Customers with Carriers, process payments, generate Waste Transfer Notes, and operate the bidding system.</li>
              <li><strong>Legal obligation (Art. 6(1)(c))</strong> — Waste carrier registration numbers are checked against the public register before an account is approved, and are stored. <RegisterCheck linkClassName="legal-link" /> Transaction records are retained for HMRC compliance.</li>
              <li><strong>Legitimate interests (Art. 6(1)(f))</strong> — to detect fraud, maintain Platform security, improve our service, and operate the leaderboard and rewards features. Our interests are balanced against your rights and do not override them.</li>
              <li><strong>Consent (Art. 6(1)(a))</strong> — where we send optional marketing communications. You may withdraw consent at any time.</li>
            </ul>
          </Section>

          <Section title="4. How Your Data Is Stored">
            <div className="legal-highlight">
              <strong>☁️ Infrastructure: Firebase / Google Cloud</strong>
              <p>All data is stored in <strong>Google Firebase</strong> (Firestore database, Firebase Authentication, and Firebase Storage), operated by Google LLC. Data is processed within the European Economic Area (EEA) and/or the United Kingdom. Google LLC is certified under the EU–US Data Privacy Framework.</p>
            </div>
            <ul className="legal-list">
              <li>Passwords are never stored by us — Firebase Authentication handles credential management using industry-standard hashing.</li>
              <li>Firestore data is encrypted at rest and in transit.</li>
              <li>Profile photos are stored in Firebase Storage and are encrypted at rest.</li>
              <li>Access to your data within the Platform is controlled by Firebase Security Rules, ensuring each user can only read/write their own records (except where job or bid data is shared between matched parties).</li>
              <li>Waste Transfer Notes are stored permanently as legal documents and may not be deleted on request (see Section 9).</li>
            </ul>
          </Section>

          <Section title="5. Payment Data and Stripe">
            <p>When Customers make payments or Carriers receive payouts through the Platform, data is shared with <strong>Stripe Payments UK Ltd</strong> as a data processor acting on our behalf.</p>
            <ul className="legal-list">
              <li>Stripe handles all card details and bank information — <BrandName inline /> never sees, stores, or processes payment card numbers or full bank account details.</li>
              <li>For Carriers completing Stripe Connect onboarding, Stripe collects identity verification data, bank account details, and tax information directly and independently. This data is governed by Stripe's own privacy policy.</li>
              <li>We store only Stripe-issued identifiers (such as payment intent IDs and Stripe account IDs) to track payment status within the Platform.</li>
              <li>Stripe's Privacy Policy is available at: <a className="legal-link" href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer">stripe.com/gb/privacy</a></li>
            </ul>
          </Section>

          <Section title="6. Profile Photos">
            <p>Users may optionally upload a profile photo through the edit profile page. Photos are stored in Firebase Storage.</p>
            <ul className="legal-list">
              <li>Profile photos are publicly visible to other logged-in users within the Platform — including on bid cards, the leaderboard, and public carrier profiles.</li>
              <li>You can change or remove your profile photo at any time via the edit profile page.</li>
              <li>Photos that violate our content guidelines (see Terms of Service Section 11) may be removed by WasteGame without notice.</li>
            </ul>
          </Section>

          <Section title="7. Carrier Referral Data">
            <p>The carrier referral system records the following data when a referral is made:</p>
            <ul className="legal-list">
              <li>The referring carrier's name and account reference.</li>
              <li>The referred carrier's name and account reference.</li>
              <li>The job context associated with the referral.</li>
            </ul>
            <p>This data is visible to the referred carrier and to WasteGame administrators. It is used solely to administer the referral reward system and is not shared with third parties.</p>
          </Section>

          <Section title="8. Who We Share Data With">
            <p>We do not sell your personal data. We share data only in the following circumstances:</p>
            <ul className="legal-list">
              <li><strong>Between matched parties:</strong> When a Customer posts a job, bid-relevant data (name, waste carrier licence, eco rate, rating, profile photo) is visible to all Carriers. When a bid is accepted, the Customer and Carrier share job details and contact-level information necessary to complete the collection.</li>
              <li><strong>Stripe Payments UK Ltd:</strong> As our payment processor, Stripe receives data necessary to process payments and verify carrier identities. Stripe operates under its own privacy policy.</li>
              <li><strong>Google Firebase:</strong> As our infrastructure provider, Google processes data on our behalf under a Data Processing Agreement compliant with UK GDPR.</li>
              <li><strong>Environment Agency or Natural Resources Wales:</strong> If a Carrier is found to have fly-tipped, we may report identifying information to the regulator for the country they are registered in, as required by law.</li>
              <li><strong>Legal obligations:</strong> We may disclose data to law enforcement or regulatory bodies where required by a court order or other legal obligation.</li>
            </ul>
            <p>We do not share data with advertisers, data brokers, or social media platforms.</p>
          </Section>

          <Section title="9. How Long We Keep Data">
            <div className="legal-table-wrap">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Data type</th>
                    <th>Retention period</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account data</td>
                    <td>Duration of account + 12 months</td>
                    <td>Account reactivation window</td>
                  </tr>
                  <tr>
                    <td>Job records</td>
                    <td>3 years from job completion</td>
                    <td>Legal disputes, audit trail</td>
                  </tr>
                  <tr>
                    <td>Waste Transfer Notes</td>
                    <td>Minimum 2 years (legal requirement)</td>
                    <td>Environmental Protection Act 1990 — duty of care records</td>
                  </tr>
                  <tr>
                    <td>Transaction records (payments)</td>
                    <td>6 years from transaction date</td>
                    <td>HMRC record-keeping requirements</td>
                  </tr>
                  <tr>
                    <td>Reviews &amp; ratings</td>
                    <td>Lifetime of Carrier account</td>
                    <td>Platform integrity</td>
                  </tr>
                  <tr>
                    <td>Bid data</td>
                    <td>2 years from bid date</td>
                    <td>Contract reference</td>
                  </tr>
                  <tr>
                    <td>Profile photos</td>
                    <td>Until removed by user or account deletion</td>
                    <td>User-controlled</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>After retention periods expire, data is deleted or anonymised.</p>
          </Section>

          <Section title="10. Your Rights Under UK GDPR">
            <p>You have the following rights. To exercise any of them, email <a className="legal-link" href="mailto:info@thewastegame.co.uk">info@thewastegame.co.uk</a> with your account email. We will respond within <strong>30 days</strong>.</p>
            <ul className="legal-list">
              <li><strong>Right of access</strong> — request a copy of all personal data we hold about you.</li>
              <li><strong>Right to rectification</strong> — request correction of inaccurate or incomplete data.</li>
              <li><strong>Right to erasure ("right to be forgotten")</strong> — request deletion of your personal data. Note: Waste Transfer Notes cannot be deleted during their mandatory 2-year retention period. Transaction records are retained for up to 6 years for HMRC compliance. Anonymisation may be applied to some records instead of full deletion.</li>
              <li><strong>Right to data portability</strong> — request your data in a structured, machine-readable format (JSON).</li>
              <li><strong>Right to restrict processing</strong> — request that we limit how we use your data while a dispute is resolved.</li>
              <li><strong>Right to object</strong> — object to processing based on legitimate interests. We will stop unless we can demonstrate compelling legitimate grounds.</li>
              <li><strong>Right to withdraw consent</strong> — where processing is based on consent, you may withdraw at any time without affecting the lawfulness of prior processing.</li>
            </ul>
            <p>You also have the right to lodge a complaint with the <strong>Information Commissioner's Office (ICO)</strong> at <a className="legal-link" href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a> or by calling 0303 123 1113.</p>
          </Section>

          <Section title="11. Account Deletion">
            <p>You may request account deletion at any time via your profile page or by emailing <a className="legal-link" href="mailto:info@thewastegame.co.uk">info@thewastegame.co.uk</a>. Deletion requests are reviewed and processed within 30 days.</p>
            <ul className="legal-list">
              <li>Job history, completed transaction records, and Waste Transfer Notes are retained after account deletion for legal compliance purposes, as described in Section 9.</li>
              <li>Eco Credits, badges, leaderboard history, and challenge progress are deleted along with your account.</li>
              <li>Your profile photo is deleted on account closure.</li>
            </ul>
          </Section>

          <Section title="12. Email Changes">
            <p>If you update your email address through the edit profile page, a verification link is sent to the new email address. Your old email address remains active on your account until the new address is verified. This prevents unauthorised email changes.</p>
          </Section>

          <Section title="13. Cookies & Local Storage">
            <p>The Platform uses only essential, functional cookies and local storage. We do not use third-party tracking or advertising cookies.</p>
            <ul className="legal-list">
              <li><strong>Firebase Authentication session tokens</strong> — stored in browser local storage to keep you logged in. Cleared on logout.</li>
              <li><strong>Theme preference</strong> — your chosen display theme (light/dark) is saved in localStorage so it persists between sessions. Contains no personal data.</li>
              <li><strong>Cookie consent state</strong> — your response to the cookie consent banner is stored in localStorage to prevent the banner from reappearing. Contains no personal data.</li>
              <li><strong>Achievement state</strong> — badge and level unlock history stored in browser local storage to prevent duplicate achievement toasts. Contains no personal data — only achievement IDs.</li>
            </ul>
            <p>No personal data is sent to third-party analytics or advertising platforms.</p>
          </Section>

          <Section title="14. Children's Privacy">
            <p>The Platform is not intended for users under 18 years of age. We do not knowingly collect personal data from children. If you believe a child has registered, contact us immediately at <a className="legal-link" href="mailto:info@thewastegame.co.uk">info@thewastegame.co.uk</a> and we will delete the account.</p>
          </Section>

          <Section title="15. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. The "last updated" date at the top of this page will reflect any changes. For material changes, we will notify registered users by email where possible. Continued use of the Platform after changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="16. Contact">
            <p>Data controller contact: <a className="legal-link" href="mailto:info@thewastegame.co.uk">info@thewastegame.co.uk</a></p>
            <p>See also our <Link className="legal-link" to="/terms">Terms of Service</Link>.</p>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
